import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { ScenarioScope, scopeKey, useScenarioScope } from '../scope';
import { ScenarioSummary } from '../types';

/**
 * Each family member's own models, which the family page lists beside the household's
 * joint ones. Read through each member's own list, keyed as their client page keys it, so
 * a save that refreshes that list refreshes these too.
 */
export function useMembersOwnScenarios(clientIds: string[], includeArchived: boolean) {
  const results = useQueries({
    queries: clientIds.map(id => ({
      queryKey: [
        ...scopeKey({ kind: 'client', id }),
        { includeArchived: includeArchived || undefined },
      ],
      placeholderData: keepPreviousData,
    })),
  });
  return {
    scenarios: results
      .flatMap(result => (result.data as ScenarioSummary[] | undefined) ?? [])
      .filter(scenario => scenario.familyId == null),
    isLoading: results.some(result => result.isLoading),
    isError: results.some(result => result.isError),
  };
}

export function useScenarios(includeArchived = false, scopeOverride?: ScenarioScope) {
  const contextScope = useScenarioScope();
  const scope = scopeOverride ?? contextScope;
  const { data, isLoading, isError } = useQuery<ScenarioSummary[]>({
    queryKey: [
      ...(scope ? scopeKey(scope) : ['clients', undefined, 'cashflow-scenarios']),
      { includeArchived: includeArchived || undefined },
    ],
    enabled: Boolean(scope),
    // Toggling "show archived" changes the key; keep the previous list on
    // screen instead of unmounting the section (and the switch) mid-click.
    placeholderData: keepPreviousData,
  });
  return { scenarios: data, isLoading, isError };
}
